import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ShieldAlert, Loader2 } from "lucide-react";

const DEFAULT_IDLE_MS = 15 * 60 * 1000; // fallback until settings load
const MAX_WARN_MS = 60 * 1000;          // warn up to 60s before logout
const POLL_MS = 30 * 1000;              // re-check the org idle-timeout setting every 30s
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

export default function IdleMonitor() {
  const { user, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [seconds, setSeconds] = useState(60);

  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdown = useRef(null);
  const lastReset = useRef(0);
  const warningRef = useRef(false);
  const idleMs = useRef(DEFAULT_IDLE_MS);
  const warnMs = useRef(MAX_WARN_MS);
  const pollRef = useRef(null);

  const applyTimeout = useCallback((mins) => {
    const newIdle = (Number(mins) || 15) * 60 * 1000;
    const changed = newIdle !== idleMs.current;
    idleMs.current = newIdle;
    warnMs.current = Math.min(MAX_WARN_MS, Math.floor(newIdle / 2));
    return changed;
  }, []);

  const clearAll = useCallback(() => {
    clearTimeout(warnTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countdown.current);
  }, []);

  const doLogout = useCallback(() => {
    clearAll();
    warningRef.current = false;
    setWarning(false);
    logout();
  }, [clearAll, logout]);

  const showWarning = useCallback(() => {
    warningRef.current = true;
    setWarning(true);
    setSeconds(Math.round(warnMs.current / 1000));
    countdown.current = setInterval(() => {
      setSeconds((s) => (s > 1 ? s - 1 : 0));
    }, 1000);
    logoutTimer.current = setTimeout(doLogout, warnMs.current);
  }, [doLogout]);

  const startTimers = useCallback(() => {
    clearAll();
    warningRef.current = false;
    setWarning(false);
    warnTimer.current = setTimeout(showWarning, Math.max(1000, idleMs.current - warnMs.current));
  }, [clearAll, showWarning]);

  const onActivity = useCallback(() => {
    if (warningRef.current) return; // ignore activity once the warning is showing
    const now = Date.now();
    if (now - lastReset.current < 1000) return; // throttle
    lastReset.current = now;
    startTimers();
  }, [startTimers]);

  const staySignedIn = useCallback(() => {
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    if (!user) { clearAll(); clearInterval(pollRef.current); return; }
    let active = true;
    api.get("/settings").then((r) => {
      if (!active) return;
      applyTimeout(r.data?.idle_timeout_minutes);
      startTimers();
    }).catch(() => { if (active) startTimers(); });

    // Live policy updates: re-check the org setting and re-arm timers if it changed.
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/settings");
        if (applyTimeout(r.data?.idle_timeout_minutes) && !warningRef.current) startTimers();
      } catch (err) { console.debug("Idle-timeout settings poll failed (ignored):", err); }
    }, POLL_MS);

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      active = false;
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(pollRef.current);
      clearAll();
    };
  }, [user, onActivity, startTimers, clearAll, applyTimeout]);

  if (!user || !warning) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="idle-warning-overlay">
      <div className="w-full max-w-sm bg-white rounded-sm shadow-xl border border-gray-200 p-7" data-testid="idle-warning-dialog">
        <div className="w-11 h-11 rounded-sm bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <h2 className="font-heading text-xl font-extrabold tracking-tight text-gray-900">Still there?</h2>
        <p className="text-gray-500 text-sm mt-1.5">
          You've been inactive. For your security you'll be signed out in{" "}
          <strong className="text-gray-900" data-testid="idle-countdown">{seconds}</strong> second{seconds === 1 ? "" : "s"}.
        </p>
        <div className="flex gap-2 mt-6">
          <button onClick={staySignedIn} data-testid="stay-signed-in-button"
            className="flex-1 bg-[#002FA7] hover:bg-[#002480] text-white py-2.5 rounded-sm font-semibold text-sm transition-colors">
            Stay signed in
          </button>
          <button onClick={doLogout} data-testid="logout-now-button"
            className="flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-sm font-semibold text-sm transition-colors">
            <Loader2 className="w-4 h-4 hidden" /> Log out
          </button>
        </div>
      </div>
    </div>
  );
}
